import React, { useId } from "react";
import PropTypes from "prop-types";

/**
 * TraderMind AI brand mark.
 *
 * Concept: an ascending "signal path" — a connected polyline of neural nodes that traces
 * an upward market peak and ends in a growth arrow. It fuses the two ideas in the name:
 * a trading chart (the rising line) and a mind / AI (the connected nodes = a signal graph).
 * Drawn on the brand indigo→violet gradient tile so it reads at 20px and at 200px.
 */
export function BrandMark({ size = 36, radius, glow = true, className = "", style = {} }) {
  const rawId = useId().replace(/[:]/g, "");
  const gid = `tm-grad-${rawId}`;
  const sid = `tm-shine-${rawId}`;
  const r = radius != null ? radius : Math.round(size * 0.3);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="TraderMind AI"
      style={{
        borderRadius: r,
        boxShadow: glow ? "0 6px 18px var(--accent-glow, rgba(99,102,241,0.35))" : "none",
        display: "block",
        ...style,
      }}
    >
      <defs>
        <linearGradient id={gid} x1="4" y1="4" x2="36" y2="36" gradientUnits="userSpaceOnUse">
          <stop stopColor="#6366F1" />
          <stop offset="0.5" stopColor="#8B5CF6" />
          <stop offset="1" stopColor="#A78BFA" />
        </linearGradient>
        <linearGradient id={sid} x1="8" y1="4" x2="20" y2="22" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFFFFF" stopOpacity="0.28" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Gradient tile */}
      <rect x="0" y="0" width="40" height="40" rx={r} fill={`url(#${gid})`} />
      {/* Top-left gloss for depth */}
      <rect x="0" y="0" width="40" height="40" rx={r} fill={`url(#${sid})`} />
      {/* Inner hairline for a crisp edge */}
      <rect
        x="0.75"
        y="0.75"
        width="38.5"
        height="38.5"
        rx={r - 0.75}
        stroke="#FFFFFF"
        strokeOpacity="0.16"
        strokeWidth="1.5"
      />

      {/* Ascending signal path */}
      <path
        d="M9 27.5 L16.5 17.5 L22.5 22 L31 11"
        stroke="#FFFFFF"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Growth arrowhead at the peak */}
      <path
        d="M31 11 L25.5 11 M31 11 L31 16.5"
        stroke="#FFFFFF"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Neural nodes at each vertex */}
      <circle cx="9" cy="27.5" r="2.5" fill="#FFFFFF" />
      <circle cx="16.5" cy="17.5" r="2.5" fill="#FFFFFF" />
      <circle cx="22.5" cy="22" r="2.5" fill="#FFFFFF" />
      <circle cx="31" cy="11" r="2.9" fill="#FFFFFF" />
      <circle cx="31" cy="11" r="1.3" fill="#7C3AED" />
    </svg>
  );
}

BrandMark.propTypes = {
  size: PropTypes.number,
  radius: PropTypes.number,
  glow: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

/**
 * Full lockup: brand mark + "TraderMind" wordmark (+ optional tagline).
 * Colors come from theme CSS variables so it adapts to light/dark automatically.
 */
export default function Logo({
  size = 36,
  showWordmark = true,
  tagline = "AI Trading",
  wordmark = "TraderMind",
  className = "",
  style = {},
  onClick,
}) {
  return (
    <div
      className={`tm-logo ${className}`}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        cursor: onClick ? "pointer" : "inherit",
        ...style,
      }}
    >
      <BrandMark size={size} />
      {showWordmark && (
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.05 }}>
          <span
            style={{
              fontWeight: 800,
              fontSize: Math.max(14, Math.round(size * 0.42)),
              letterSpacing: "-0.02em",
              color: "var(--text-primary, #E8E4F0)",
              whiteSpace: "nowrap",
            }}
          >
            {wordmark}
          </span>
          {tagline && (
            <span
              style={{
                fontSize: Math.max(9, Math.round(size * 0.27)),
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--accent-primary, #8B5CF6)",
                whiteSpace: "nowrap",
              }}
            >
              {tagline}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

Logo.propTypes = {
  size: PropTypes.number,
  showWordmark: PropTypes.bool,
  tagline: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([false])]),
  wordmark: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
};
